import React,{useState,useRef,useCallback,useEffect} from 'react';
import {PitchBackdrop} from '../components/Shared';
import {api} from '../lib/api';
import {LOGO_ICON_DATA_URI} from '../lib/assets';
import {computeAccessStatus,fmtTenure,trialDaysLeft} from '../lib/utils';

export function AdminApp({auth,onSignOut}){
  const [orgs,setOrgs]=useState(null); // null = loading
  const [detailOrgId,setDetailOrgId]=useState(null);
  const [search,setSearch]=useState("");
  const [sortKey,setSortKey]=useState("name"); // name|teams|players|coaches|tier|created
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<640);
  useEffect(()=>{const h=()=>setIsMobile(window.innerWidth<640);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h);},[]);
  const [mode,setMode]=useState(()=>{try{return localStorage.getItem("sm_theme_mode")||"light";}catch(e){return "light";}});
  useEffect(()=>{document.documentElement.setAttribute("data-theme-mode",mode);try{localStorage.setItem("sm_theme_mode",mode);}catch(e){}},[mode]);
  const ac="#0ea5e9";
  const AS={accentColor:ac,bgColor:"var(--c-bg0)",surfaceColor:"var(--c-surface)",borderColor:"var(--c-border)"};
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};

  useEffect(()=>{ load(); },[]);
  async function load(){
    setOrgs(null);
    const [orgRows,coachRows,teamRows,playerRows,ocRows]=await Promise.all([
      api.get(auth.token,"organisations","select=*&order=created_at.desc"),
      api.get(auth.token,"coaches","select=id,name,email"),
      api.get(auth.token,"teams","select=id,org_id,name,badge,gender,age_category"),
      api.get(auth.token,"players","select=id,org_id"),
      api.get(auth.token,"org_coaches","select=id,org_id,coach_id,role"),
    ]);
    const coachById=Object.fromEntries((coachRows||[]).map(c=>[c.id,c]));
    const merged=(orgRows||[]).map(o=>{
      const teams=(teamRows||[]).filter(t=>t.org_id===o.id);
      const players=(playerRows||[]).filter(p=>p.org_id===o.id);
      const orgCoachRows=(ocRows||[]).filter(oc=>oc.org_id===o.id).map(oc=>({...oc,coach:coachById[oc.coach_id]}));
      return{
        ...o,
        teams,
        teamCount:teams.length,
        playerCount:players.length,
        coachCount:orgCoachRows.length,
        orgCoaches:orgCoachRows,
        headCoach:coachById[o.head_coach_id]||null,
      };
    });
    setOrgs(merged);
  }

  // Manual override — until Stripe is wired up, an admin can flip a club's subscription
  // on (e.g. they paid by bank transfer) or off directly from here.
  async function setOrgSubscription(orgId,active){
    const nowIso=new Date().toISOString();
    const updates=active?{subscription_active:true,subscription_started_at:nowIso}:{subscription_active:false};
    const res=await api.patch(auth.token,"organisations",orgId,updates);
    if(res)setOrgs(prev=>prev.map(o=>o.id===orgId?{...o,...updates}:o));
  }

  const filtered=(orgs||[]).filter(o=>!search.trim()||o.name.toLowerCase().includes(search.trim().toLowerCase())||(o.headCoach?.name||"").toLowerCase().includes(search.trim().toLowerCase()));
  const sorted=[...filtered].sort((a,b)=>{
    if(sortKey==="teams")return b.teamCount-a.teamCount;
    if(sortKey==="players")return b.playerCount-a.playerCount;
    if(sortKey==="coaches")return b.coachCount-a.coachCount;
    if(sortKey==="status")return computeAccessStatus(a).localeCompare(computeAccessStatus(b));
    if(sortKey==="created")return new Date(b.created_at||0)-new Date(a.created_at||0);
    return (a.name||"").localeCompare(b.name||"");
  });

  const detailOrg=(orgs||[]).find(o=>o.id===detailOrgId)||null;
  const totals=(orgs||[]).reduce((acc,o)=>({orgs:acc.orgs+1,teams:acc.teams+o.teamCount,players:acc.players+o.playerCount,coaches:acc.coaches+o.coachCount}),{orgs:0,teams:0,players:0,coaches:0});

  return(<div style={{minHeight:"100vh",background:mode==="dark"?AS.bgColor:"transparent",fontFamily:"'Inter',system-ui,sans-serif",color:"var(--c-text)",position:"relative"}}>
    {mode!=="dark"&&<PitchBackdrop/>}
    <header style={{position:isMobile?"sticky":"relative",top:isMobile?0:undefined,zIndex:isMobile?120:1,background:mode==="dark"?AS.surfaceColor:"rgba(255,255,255,0.94)",backdropFilter:mode==="dark"?undefined:"blur(8px)",borderBottom:`1px solid ${AS.borderColor}`,padding:"0 14px"}}>
      <div style={{maxWidth:1200,margin:"0 auto",display:"flex",alignItems:"center",gap:9,height:isMobile?50:55}}>
        <img src={LOGO_ICON_DATA_URI} alt="pitchMGR" style={{width:isMobile?22:26,height:isMobile?22:26,borderRadius:6,flexShrink:0}}/>
        {!isMobile&&<span style={{fontWeight:700,fontSize:16,letterSpacing:"-0.3px"}}>pitchMGR <span style={{color:ac,fontWeight:600}}>Admin</span></span>}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
          {!isMobile&&<span style={{fontSize:11,fontWeight:600,color:"var(--c-text2)"}}>{auth.admin?.name||auth.user?.email}</span>}
          <button onClick={()=>setMode(m=>m==="dark"?"light":"dark")} style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:12}} title={mode==="dark"?"Switch to day mode":"Switch to night mode"}>{mode==="dark"?"☀️":"🌙"}</button>
          <button onClick={onSignOut} style={{padding:"5px 8px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",cursor:"pointer",fontSize:11}} title="Sign out">↩</button>
        </div>
      </div>
    </header>

    <div style={{position:"relative",zIndex:1,maxWidth:1200,margin:mode==="dark"?"0 auto":"16px auto",padding:isMobile?"10px 10px 24px":"20px",background:mode==="dark"?"transparent":"rgba(255,255,255,0.93)",borderRadius:mode==="dark"?0:18,boxShadow:mode==="dark"?"none":"0 10px 40px rgba(20,83,45,0.12)"}}>

      <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(4,1fr)",gap:10,marginBottom:16}}>
        {[["Organisations",totals.orgs],["Teams",totals.teams],["Players",totals.players],["Coaches",totals.coaches]].map(([label,val])=>(
          <div key={label} style={{...C,padding:14}}>
            <div style={{fontSize:20,fontWeight:800}}>{orgs===null?"—":val}</div>
            <div style={{fontSize:10,fontWeight:600,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginTop:2}}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{...C,padding:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <h3 style={{margin:0,fontSize:10,fontWeight:600,color:"var(--c-text2)",textTransform:"uppercase",letterSpacing:"0.05em"}}>Registered Organisations</h3>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search club or head coach…" style={{padding:"6px 10px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:11,minWidth:180}}/>
            <select value={sortKey} onChange={e=>setSortKey(e.target.value)} style={{padding:"6px 8px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text3)",fontSize:11,cursor:"pointer"}}>
              <option value="name">Sort: Name</option><option value="teams">Sort: Teams</option><option value="players">Sort: Players</option><option value="coaches">Sort: Coaches</option><option value="status">Sort: Status</option><option value="created">Sort: Newest</option>
            </select>
          </div>
        </div>

        {orgs===null?(
          <div style={{padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>Loading…</div>
        ):sorted.length===0?(
          <div style={{padding:30,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No organisations {search?"match your search":"registered yet"}.</div>
        ):(
        <div style={{overflowX:"auto"}}>
        <div style={{minWidth:640}}>
          <div style={{display:"grid",gridTemplateColumns:"1.6fr 1.3fr 70px 70px 70px 110px",gap:8,padding:"4px 8px",fontSize:8,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",borderBottom:`1px solid ${AS.borderColor}`,marginBottom:3}}>
            <span>Organisation</span><span>Head Coach</span><span style={{textAlign:"center"}}>Teams</span><span style={{textAlign:"center"}}>Players</span><span style={{textAlign:"center"}}>Coaches</span><span style={{textAlign:"center"}}>Status</span>
          </div>
          {sorted.map(o=>{
            const status=computeAccessStatus(o);
            const statusColor=status==="expired"?"#ef4444":status==="trial"?"#f59e0b":"#22c55e";
            const statusLabel=status==="expired"?"Expired":status==="trial"?`Trial · ${trialDaysLeft(o)}d`:"Subscribed";
            return(
            <div key={o.id} onClick={()=>setDetailOrgId(o.id)} style={{display:"grid",gridTemplateColumns:"1.6fr 1.3fr 70px 70px 70px 110px",gap:8,padding:"10px 8px",alignItems:"center",borderBottom:`1px solid ${AS.bgColor}`,cursor:"pointer"}}>
              <span style={{fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><span>{o.badge||"⭐"}</span>{o.name}</span>
              <span style={{fontSize:11,color:"var(--c-text2)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.headCoach?.name||"—"}</span>
              <span style={{textAlign:"center",fontWeight:700,fontSize:12}}>{o.teamCount}</span>
              <span style={{textAlign:"center",fontWeight:700,fontSize:12}}>{o.playerCount}</span>
              <span style={{textAlign:"center",fontWeight:700,fontSize:12}}>{o.coachCount}</span>
              <span style={{textAlign:"center"}}><span style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:10,background:statusColor+"22",color:statusColor}}>{statusLabel}</span></span>
            </div>
            );
          })}
        </div>
        </div>
        )}
      </div>
    </div>

    {detailOrg&&(
      <div onClick={()=>setDetailOrgId(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:400,display:"flex",alignItems:isMobile?"flex-end":"center",justifyContent:"center",padding:isMobile?0:16}}>
        <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:560,maxHeight:isMobile?"88vh":"85vh",overflowY:"auto",background:"var(--c-surface)",borderRadius:isMobile?"16px 16px 0 0":16,border:`1px solid ${AS.borderColor}`,padding:22,boxSizing:"border-box"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:26}}>{detailOrg.badge||"⭐"}</span>
              <div>
                <div style={{fontSize:17,fontWeight:800}}>{detailOrg.name}</div>
                <div style={{fontSize:11,color:"var(--c-muted)"}}>Registered {detailOrg.created_at?new Date(detailOrg.created_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}):"—"}</div>
              </div>
            </div>
            <button onClick={()=>setDetailOrgId(null)} style={{background:"none",border:"none",color:"var(--c-muted)",fontSize:18,cursor:"pointer",padding:"2px 6px"}}>✕</button>
          </div>

          <div style={{...C,padding:14,marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Head Coach</div>
            {detailOrg.headCoach?(<>
              <div style={{fontSize:13,fontWeight:700}}>{detailOrg.headCoach.name}</div>
              <div style={{fontSize:11,color:"var(--c-muted)"}}>{detailOrg.headCoach.email}</div>
            </>):<div style={{fontSize:12,color:"var(--c-muted2)"}}>No head coach on record.</div>}
          </div>

          <div style={{...C,padding:14,marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>Billing</div>
            {(()=>{
              const status=computeAccessStatus(detailOrg);
              const statusColor=status==="expired"?"#ef4444":status==="trial"?"#f59e0b":"#22c55e";
              return(<>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:13,fontWeight:800,color:statusColor}}>{status==="expired"?"Trial Expired":status==="trial"?"Free Trial":"Subscribed"}</span>
                  {status==="trial"&&<span style={{fontSize:11,color:"var(--c-muted)"}}>{trialDaysLeft(detailOrg)} day{trialDaysLeft(detailOrg)===1?"":"s"} left</span>}
                </div>
                <div style={{fontSize:11,color:"var(--c-muted)"}}>
                  {status==="active"?(<>Subscribed for <b style={{color:"var(--c-text2)"}}>{fmtTenure(detailOrg.subscription_started_at||detailOrg.created_at)}</b>{detailOrg.subscription_started_at?` (since ${new Date(detailOrg.subscription_started_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})})`:""}</>)
                  :(<>Trial ends {detailOrg.trial_ends_at?new Date(detailOrg.trial_ends_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"}):"—"}</>)}
                </div>
                <div style={{marginTop:10}}>
                  {status==="active"?(
                    <button onClick={()=>setOrgSubscription(detailOrg.id,false)} style={{padding:"7px 13px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontWeight:700,fontSize:11,cursor:"pointer"}}>Deactivate Subscription</button>
                  ):(
                    <button onClick={()=>setOrgSubscription(detailOrg.id,true)} style={{padding:"7px 13px",borderRadius:7,border:"none",background:"#22c55e",color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Activate Subscription (Admin Override)</button>
                  )}
                </div>
              </>);
            })()}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
            {[["Teams",detailOrg.teamCount],["Players",detailOrg.playerCount],["Coaches",detailOrg.coachCount]].map(([label,val])=>(
              <div key={label} style={{...C,padding:"10px 8px",textAlign:"center"}}>
                <div style={{fontSize:17,fontWeight:800}}>{val}</div>
                <div style={{fontSize:9,fontWeight:600,color:"var(--c-text)",textTransform:"uppercase"}}>{label}</div>
              </div>
            ))}
          </div>

          {detailOrg.teams.length>0&&(
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Teams</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {detailOrg.teams.map(t=>(
                  <div key={t.id} style={{...C,padding:"8px 11px",display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:14}}>{t.badge||"⭐"}</span>
                    <span style={{fontSize:12,fontWeight:600,flex:1}}>{t.name}</span>
                    <span style={{fontSize:10,color:"var(--c-muted)"}}>{[t.gender,t.age_category].filter(Boolean).join(" · ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detailOrg.orgCoaches.length>0&&(
            <div>
              <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>Coaches</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {detailOrg.orgCoaches.map(oc=>(
                  <div key={oc.id} style={{...C,padding:"8px 11px",display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:12,fontWeight:600,flex:1}}>{oc.coach?.name||"—"}</span>
                    <span style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:8,background:oc.coach_id===detailOrg.head_coach_id?ac+"22":"var(--c-bg0)",color:oc.coach_id===detailOrg.head_coach_id?ac:"var(--c-muted)"}}>{oc.coach_id===detailOrg.head_coach_id?"Owner":oc.role==="head"?"Admin":"Assistant"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    )}
  </div>);
}

// ── Root ──────────────────────────────────────────────────────────────────────

